import { UserRole } from '../../types/permissions';
import { isValidPermissionId } from '../permissionMapper';

/**
 * Resultado da validação de mensagens do WebView
 */
export interface WebViewMessageValidation {
  isValid: boolean;
  reason?: string;
  sanitizedData?: any;
}

/**
 * Estrutura esperada para mensagens de carregarInterface
 */
interface CarregarInterfaceData {
  perm_id: number;
  permissionsConfig?: any[];
  [key: string]: any;
}

/**
 * Valida mensagens recebidas do WebView C# para prevenir sobrescrita não autorizada
 * @param message Mensagem recebida do WebView
 * @param currentUserPermId ID da permissão atual do usuário (da API)
 * @param currentUserIsAdmin Se o usuário atual é admin
 * @returns Resultado da validação
 */
export function validateWebViewMessage(
  message: any,
  currentUserPermId?: number,
  currentUserIsAdmin?: boolean
): WebViewMessageValidation {
  
  // Validação básica de estrutura
  if (!message || typeof message !== 'object') {
    return {
      isValid: false,
      reason: 'Mensagem inválida ou não é um objeto'
    };
  }

  // Se não é carregarInterface, permite (outras mensagens são válidas)
  if (message.action !== 'carregarInterface') {
    return { isValid: true };
  }

  // Validação específica para carregarInterface
  if (!message.data) {
    return {
      isValid: false,
      reason: 'Mensagem carregarInterface sem dados'
    };
  }

  const data = message.data as CarregarInterfaceData;

  // Validação do perm_id
  if (typeof data.perm_id !== 'number') {
    return {
      isValid: false,
      reason: 'perm_id deve ser um número'
    };
  }

  // Validação se o perm_id é válido no sistema
  if (!isValidPermissionId(data.perm_id)) {
    return {
      isValid: false,
      reason: `perm_id ${data.perm_id} não é válido no sistema`
    };
  }

  // PROTEÇÃO CRÍTICA: Prevenir escalação de privilégios
  if (currentUserPermId && currentUserIsAdmin !== true) {
    // Usuários não-admin não podem receber permissões diferentes da sua
    if (data.perm_id !== currentUserPermId) {
      return {
        isValid: false,
        reason: `Tentativa de alteração não autorizada: usuário perm_id ${currentUserPermId} não pode receber perm_id ${data.perm_id}`
      };
    }
  }

  // PROTEÇÃO: Admin não pode receber permissão menor que admin (perm_id 2)
  if (currentUserIsAdmin === true && data.perm_id !== 2) {
    return {
      isValid: false,
      reason: `Admin não pode receber permissão não-admin (perm_id ${data.perm_id})`
    };
  }

  // Se chegou até aqui, a mensagem é válida
  return {
    isValid: true,
    sanitizedData: {
      perm_id: data.perm_id,
      permissionsConfig: Array.isArray(data.permissionsConfig) ? data.permissionsConfig : undefined
    }
  };
}

/**
 * Versão simplificada para casos onde não temos dados do usuário atual
 * Aplica apenas validações básicas de estrutura e formato
 */
export function validateWebViewMessageBasic(message: any): WebViewMessageValidation {
  return validateWebViewMessage(message);
}
